import { signal, useSignalEffect } from '@preact/signals'
import { useCallback, useMemo } from 'preact/hooks'
import type AppStore from '../store/AppStore'
import type { FlowProps } from './flowTypes'

export function autoExpandVisible(isEmptyList: boolean, isCollapsed: boolean): boolean {
  return !isEmptyList && !isCollapsed
}

export function useAutoExpandFlow(store: AppStore): FlowProps {
  const isCollapsed = useMemo(() => signal(false), [])

  const onExpandChange = useCallback(
    async (isExpanded: boolean) => {
      if (!isExpanded) {
        isCollapsed.value = true
      }
      if (isExpanded) {
        await store.pauseScanning()
      } else {
        await store.resumeScanning()
      }
    },
    [store, isCollapsed]
  )

  useSignalEffect(() => {
    const qty = store.listStore?.totalQuantity?.value ?? 0
    if (qty > 0) {
      isCollapsed.value = false
      void store.pauseScanning()
    }
  })

  const onClearList = useCallback(async () => {
    store.listStore?.removeAll()
    isCollapsed.value = true
    await store.resumeScanning()
  }, [store, isCollapsed])

  const onResumeScanning = useCallback(async () => {
    isCollapsed.value = true
    await store.resumeScanning()
  }, [store, isCollapsed])

  const totalQuantity = store.listStore?.totalQuantity ?? signal(0)
  const items = store.listStore?.resultItems ?? signal([])
  const isEmptyList = store.listStore?.isEmpty.value ?? true

  return {
    totalQuantity,
    items,
    visible: autoExpandVisible(isEmptyList, isCollapsed.value),
    expanded: true,
    onExpandChange,
    onClearList,
    onResumeScanning,
    showDrawerBottomSheetArrow: false,
  }
}
